import { categoryLabel } from './expense-categories.js';

/**
 * @typedef {{ id: string, label: string, amount: number, pct: number }} CategoryBreakdown
 * @typedef {{ dayKey: string, amount: number, pct: number }} DayBreakdown
 */

/**
 * @param {Array<{ amount_eur: number, category?: string, day_key: string }>} expenses
 * @param {Array<{ date: string }>} days
 */
export function computeExpenseAnalytics(expenses, days = []) {
  const total = expenses.reduce((sum, e) => sum + Number(e.amount_eur), 0);
  const count = expenses.length;

  const byCategory = new Map();
  for (const expense of expenses) {
    const cat = expense.category ?? 'altro';
    byCategory.set(cat, (byCategory.get(cat) ?? 0) + Number(expense.amount_eur));
  }

  const categories = [...byCategory.entries()]
    .map(([id, amount]) => ({
      id,
      label: categoryLabel(id),
      amount,
      pct: total > 0 ? (amount / total) * 100 : 0,
    }))
    .sort((a, b) => b.amount - a.amount);

  const byDay = new Map();
  for (const expense of expenses) {
    byDay.set(expense.day_key, (byDay.get(expense.day_key) ?? 0) + Number(expense.amount_eur));
  }

  const dayOrder = new Map(days.map((d, i) => [d.date, i]));
  const daysBreakdown = [...byDay.entries()]
    .map(([dayKey, amount]) => ({
      dayKey,
      amount,
      pct: total > 0 ? (amount / total) * 100 : 0,
    }))
    .sort((a, b) => (dayOrder.get(a.dayKey) ?? 999) - (dayOrder.get(b.dayKey) ?? 999));

  const daysWithExpenses = byDay.size;
  const tripDayCount = Math.max(days.length, 1);

  return {
    total,
    count,
    categories,
    daysBreakdown,
    daysWithExpenses,
    avgPerActiveDay: daysWithExpenses > 0 ? total / daysWithExpenses : 0,
    avgPerTripDay: total / tripDayCount,
    maxCategoryAmount: categories[0]?.amount ?? 0,
    maxDayAmount: daysBreakdown.reduce((max, d) => Math.max(max, d.amount), 0),
  };
}
